/**
 * Runner tracking use case
 * Application layer service for managing runner tracking operations
 */

import { runnerApiClient } from '../infrastructure/ApiClient.js'
import { Runner } from '../domain/models/Runner.js'
import { TrackPoint } from '../domain/models/TrackPoint.js'
import { Checkpoint } from '../domain/models/Checkpoint.js'
import { calculatePerformanceMetrics } from '../domain/services/PerformanceCalculator.js'
import { findLatestPositionByTime } from '../utils/gpsUtils.js'

/**
 * Runner tracking service
 */
export class RunnerTrackingService {
  /**
   * Get complete runner tracking data with performance metrics
   * @param {string} eventAlias - Event alias
   * @param {string|number} runnerNumber - Runner number
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>} Complete runner data for UI
   */
  async getRunnerTrackingData(eventAlias, runnerNumber, options = {}) {
    const defaultOptions = {
      includeTracks: 1,
      includeSplits: 1,
      limit: 100,
      ...options
    }
    
    const apiData = await runnerApiClient.getRunnerData(eventAlias, runnerNumber, defaultOptions)
    return this.transformToUIData(apiData)
  }

  /**
   * Transform API response to UI-compatible format
   * @param {Object} apiData - Raw API response
   * @returns {Object} Transformed data for UI components
   */
  transformToUIData(apiData) {
    if (!apiData || !apiData.runner) {
      throw new Error('Nieprawidłowe dane z API')
    }

    const { runner, tracks = [], splits = [], ranking = {}, meta = {} } = apiData
    console.log("runner startTime", runner.startTime);

    if (!runner) {
      throw new Error('Brak danych zawodnika w odpowiedzi API')
    }
    // Create domain models
    const runnerModel = Runner.fromApiData(runner)
    const trackPoints = tracks.map(track => TrackPoint.fromApiData(track)).filter(t => t.isValid)
    const checkpoints = splits.map((split, index) => Checkpoint.fromApiData(split, index))

    // Calculate performance metrics

    const performanceMetrics = calculatePerformanceMetrics(tracks, splits, runner)

    // Get latest position for map (find by most recent timestamp)
    const latestPosition = findLatestPositionByTime(trackPoints)

    // Transform checkpoints for UI table
    const uiCheckpoints = checkpoints.map((checkpoint, index) => ({
      id: checkpoint.id,
      number: checkpoint.number,
      name: checkpoint.name,
      distance: checkpoint.formattedDistance,
      time: checkpoint.time ? checkpoint.checkpointTime : checkpoint.formattedTime,
      splitTime: index > 0 ? 
        Checkpoint.calculateSplitTime(checkpoint, checkpoints[index - 1]) : 
        checkpoint.formattedSplitTime,
      status: checkpoint.status
    }))

    return {
      // Runner basic info
      firstName: runnerModel.firstName,
      lastName: runnerModel.lastName,
      number: runnerModel.number,
      status: runnerModel.status,
      categoryName: runnerModel.categoryName,
      startTime: runnerModel.startTime,
      
      // Performance data
      ...performanceMetrics,
      
      // Event info
      eventInfo: `${meta.eventName || 'Wydarzenie'} - ${performanceMetrics.distance} ${new Date().toLocaleDateString('pl-PL')}`,
      
      // Checkpoints for table display
      checkpoints: uiCheckpoints,
      
      // Ranking information
      ranking: {
        position: ranking.overallPosition,
        total: ranking.totalParticipants
      },
      
      // Raw tracking data for map (converted back to plain objects for map compatibility)
      tracks: trackPoints.map(tp => ({
        latitude: tp.latitude,
        longitude: tp.longitude,
        altitude: tp.altitude,
        time: tp.time,
        speed: tp.speed,
        direction: tp.direction,
        distance: tp.distance,
        progress: tp.progress
      })),
      latestPosition: latestPosition ? {
        latitude: latestPosition.latitude,
        longitude: latestPosition.longitude,
        altitude: latestPosition.altitude,
        time: latestPosition.time,
        speed: latestPosition.speed,
        direction: latestPosition.direction,
        distance: latestPosition.distance,
        progress: latestPosition.progress
      } : null,
      
      // API metadata
      meta
    }
  }
}

// Export singleton instance
export const runnerTrackingService = new RunnerTrackingService()